<template>
  <div>
		<div v-if="!noTrigger">
			<v-tooltip v-if="smallBtn" bottom>
				<template v-slot:activator="{ on, attrs }">
					<button
						type="button"
						class="plano-trigger-btn plano-trigger-btn--small"
						:class="{ 'plano-trigger-btn--active': planoEntrega }"
						v-bind="attrs"
						v-on="on"
						@click="openModal"
					>
						<v-icon size="13" :color="planoEntrega ? 'white' : '#2D4B95'" class="mr-1">
							{{ planoEntrega ? (viewOnly ? 'mdi-eye' : 'mdi-pencil') : 'mdi-map-marker-plus' }}
						</v-icon>
						{{ planoEntrega ? (viewOnly ? 'Ver' : 'Editar') : 'Configurar' }}
					</button>
				</template>
				<span>{{ planoEntrega ? (viewOnly ? 'Ver Emplazamiento' : 'Editar Emplazamiento') : 'Configurar Emplazamiento' }}</span>
			</v-tooltip>

			<button
				v-else
				type="button"
				class="plano-trigger-btn"
				:class="{ 'plano-trigger-btn--active': planoEntrega }"
				@click="openModal"
			>
				<v-icon size="14" :color="planoEntrega ? 'white' : '#2D4B95'" class="mr-1">
					{{ planoEntrega ? (viewOnly ? 'mdi-eye' : 'mdi-pencil') : 'mdi-map-marker-plus' }}
				</v-icon>
				{{ planoEntrega ? (viewOnly ? 'Ver Emplazamiento' : 'Editar Emplazamiento') : 'Configurar Emplazamiento' }}
			</button>
		</div>

    <!-- Fullscreen Dialog -->
    <v-dialog
      v-model="showModal"
      fullscreen
      transition="dialog-bottom-transition"
      scrollable
      persistent
    >
      <v-card style="display: flex; flex-direction: column; height: 100vh; overflow: hidden;">

        <!-- Toolbar -->
        <div class="app-toolbar">
          <div class="app-toolbar__title">
            <v-icon small class="mr-2" style="opacity: 0.8; color: white;">mdi-map-marker-outline</v-icon>
            <span>{{ viewOnly ? 'Ver' : (planoEntrega ? 'Editar' : 'Configurar') }} Emplazamiento</span>
          </div>
          <div class="app-toolbar__actions">
            <button class="app-toolbar__btn app-toolbar__btn--outline" @click="closeModal">
              <v-icon x-small style="margin-right: 4px;">mdi-close-circle-outline</v-icon>
              Cerrar
            </button>
            <button
              v-if="!viewOnly"
              class="app-toolbar__btn app-toolbar__btn--solid"
              :disabled="!internalSelectedPlano || loading"
              @click="triggerSave"
            >
              <v-icon x-small style="margin-right: 4px;">mdi-content-save-outline</v-icon>
              {{ loading ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>

        <!-- Controls Bar -->
        <div class="controls-bar px-4 py-2" style="flex-shrink: 0; border-bottom: 1px solid #e0e0e0; background: #fafafa;">
          <div class="d-flex align-center" style="gap: 12px; flex-wrap: wrap;">

            <!-- Plano Selector -->
            <div style="flex: 1; min-width: 220px; max-width: 420px;">
              <v-autocomplete
                v-model="internalSelectedPlano"
                :items="planos"
                item-text="title"
                item-value="id"
                label="Seleccione el plano"
                prepend-inner-icon="mdi-floor-plan"
                outlined
                dense
                hide-details
                :disabled="viewOnly && !!internalSelectedPlano && !allowPlanoChange"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-avatar size="28" :color="planoIsForge(item) ? 'blue lighten-5' : 'green lighten-5'" rounded>
                      <v-icon :color="planoIsForge(item) ? 'blue darken-1' : 'green darken-1'" size="15">
                        {{ planoIsForge(item) ? 'mdi-file-cad' : 'mdi-image-outline' }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip x-small :color="planoIsForge(item) ? 'blue lighten-4' : 'green lighten-4'" :text-color="planoIsForge(item) ? 'blue darken-3' : 'green darken-3'">
                          {{ planoIsForge(item) ? 'Visor Autodesk' : 'Imagen' }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title class="grey--text text-center">Sin planos disponibles</v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <v-divider vertical class="mx-1" style="height: 36px;"></v-divider>

            <!-- Color -->
            <div class="d-flex align-center" style="gap: 8px; flex-shrink: 0;">
              <span class="text-caption text--secondary">Color:</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    @click="!viewOnly && (showColorPicker = true)"
                    class="color-swatch"
                    :style="{ backgroundColor: lineColor, cursor: viewOnly ? 'default' : 'pointer' }"
                  ></div>
                </template>
                <span>{{ viewOnly ? 'Color del área' : 'Cambiar color del área' }}</span>
              </v-tooltip>
            </div>

            <!-- Opacidad -->
            <div class="d-flex align-center" style="gap: 8px; flex-shrink: 0; width: 180px;">
              <v-icon small class="text--secondary">mdi-opacity</v-icon>
              <span class="text-caption text--secondary" style="white-space: nowrap;">Opacidad:</span>
              <v-slider
                v-model="fillOpacity"
                min="0.05"
                max="1"
                step="0.05"
                hide-details
                dense
                color="primary"
                style="margin: 0;"
                :disabled="viewOnly"
              ></v-slider>
              <span class="text-caption font-weight-medium" style="width: 28px; flex-shrink: 0;">{{ Math.round(fillOpacity * 100) }}%</span>
            </div>

            <v-divider v-if="!isForgeMode" vertical class="mx-1" style="height: 36px;"></v-divider>

            <!-- Zoom controls (solo ImageViewer) -->
            <div v-if="!isForgeMode" class="d-flex align-center" style="gap: 4px; flex-shrink: 0;">
              <button class="cpm-zoom-btn" @click="zoomOut" :disabled="!internalSelectedPlano" title="Alejar">
                <v-icon size="16">mdi-minus</v-icon>
              </button>
              <span class="text-caption font-weight-medium" style="width: 44px; text-align: center; flex-shrink: 0;">
                {{ Math.round(viewerZoomLevel * 100) }}%
              </span>
              <button class="cpm-zoom-btn" @click="zoomIn" :disabled="!internalSelectedPlano" title="Acercar">
                <v-icon size="16">mdi-plus</v-icon>
              </button>
            </div>

            <v-divider v-if="!viewOnly" vertical class="mx-1" style="height: 36px;"></v-divider>

            <!-- Hints (navegación / edición) -->
            <div v-if="!viewOnly && internalSelectedPlano" class="d-flex align-center" style="gap: 6px; flex-shrink: 0;">
              <template v-if="isForgeMode">
                <template v-if="allowedEditing">
                  <span class="cpm-hint cpm-hint--blue">
                    <v-icon size="12" color="#2D4B95">mdi-cursor-default-click</v-icon>
                    Click: añadir punto
                  </span>
                  <span class="cpm-hint cpm-hint--grey">
                    <v-icon size="12" color="#555">mdi-drag</v-icon>
                    Arrastrar vértices
                  </span>
                  <span class="cpm-hint cpm-hint--red">
                    <v-icon size="12" color="#c62828">mdi-keyboard-return</v-icon>
                    Enter / doble click: cerrar
                  </span>
                </template>
                <span v-else class="cpm-hint cpm-hint--grey">
                  <v-icon size="12" color="#555">mdi-pan</v-icon>
                  Mover y encuadrar; active «Insertar puntos» para editar
                </span>
              </template>
              <template v-else>
                <span class="cpm-hint cpm-hint--blue">
                  <v-icon size="12" color="#2D4B95">mdi-cursor-pointer</v-icon>
                  Añadir punto
                </span>
                <span class="cpm-hint cpm-hint--grey">
                  <v-icon size="12" color="#555">mdi-pan</v-icon>
                  Mover / Pan
                </span>
                <span class="cpm-hint cpm-hint--red">
                  <v-icon size="12" color="#c62828">mdi-close-circle-outline</v-icon>
                  Clic derecho: borrar
                </span>
              </template>
            </div>

						<div class="d-flex justify-end" style="gap: 6px; flex-shrink: 0;width: 100%;" v-if="$vuetify.breakpoint.smAndDown">
							<!-- Limpiar polígono -->
							<div v-if="!viewOnly && internalSelectedPlano" style="flex-shrink: 0;">
								<button class="cpm-clear-btn" @click="clearCurrentPolygon" :disabled="!isForgeMode && viewerNodeCount === 0">
									<v-icon size="13" class="mr-1">mdi-eraser</v-icon>
									Limpiar polígono
								</button>
							</div>

                            <!-- Crear / editar polígono -->
                            <div v-if="!viewOnly && internalSelectedPlano && isForgeMode" style="flex-shrink: 0;">
								<button class="cpm-edit-btn" @click="togglePolygonEdit" :disabled="!isForgeMode">
									<v-icon size="13" class="mr-1">mdi-shape-polygon-plus</v-icon>
                  {{ isEditingPolygon ? 'Terminar edición' : 'Insertar puntos' }}
								</button>
							</div>
						</div>

						<div v-else-if="!viewOnly && internalSelectedPlano" style="flex-shrink: 0;" class="ml-auto">
							<button class="cpm-clear-btn" @click="clearCurrentPolygon" :disabled="!isForgeMode && viewerNodeCount === 0">
								<v-icon size="13" class="mr-1">mdi-eraser</v-icon>
								Limpiar polígono
							</button>
              <button v-if="isForgeMode" class="cpm-edit-btn ml-2" @click="togglePolygonEdit">
                <v-icon size="13" class="mr-1">mdi-shape-polygon-plus</v-icon>
                {{ isEditingPolygon ? 'Terminar edición' : 'Insertar puntos' }}
              </button>
						</div>

          </div>
        </div>

        <!-- Viewer Area -->
        <div style="flex: 1; overflow: hidden; min-height: 0; display: flex; flex-direction: row; position: relative;">

          <!-- Canvas principal -->
          <div ref="viewerWrapper" style="flex: 1; overflow: hidden; position: relative;">
            <template v-if="showModal && internalSelectedPlano">

              <!-- Forge viewer -->
              <ForgePolygonViewer
                v-if="isForgeMode"
                :key="`forge-${internalSelectedPlano}`"
                ref="viewer"
                :forge-file-urn="selectedPlanoForgeUrn"
                :plano-data="forgeViewerPlanoData"
                :read-only="viewOnly"
                :line-color="lineColor"
                :fill-opacity="fillOpacity"
								:setting-polygon="allowedEditing"
                style="height: 100%;"
                @save-plano="onSubViewerSave"
								@polygon-closed="onPolygonClosed"
              />

              <!-- Image viewer -->
              <ImagePolygonViewer
                v-else
                :key="`image-${internalSelectedPlano}`"
                ref="viewer"
                :plano="selectedPlanoObj"
                :plano-data="imageViewerPlanoData"
                :read-only="viewOnly"
                :all-polygons="visibleAllPolygons"
                :line-color="lineColor"
                :fill-opacity="fillOpacity"
                style="height: 100%;"
                @save-plano="onSubViewerSave"
                @zoom-change="viewerZoomLevel = $event"
                @nodes-change="viewerNodeCount = $event"
              />

            </template>

            <!-- Placeholder sin plano seleccionado -->
            <div v-else class="cpm-placeholder">
              <v-icon size="56" color="grey lighten-1">mdi-floor-plan</v-icon>
              <div class="text-subtitle-2 mt-3 grey--text">Seleccione un plano para comenzar</div>
            </div>
          </div>

          <!-- ── DESKTOP: Sidebar fijo ── -->
          <!-- <div
            v-if="internalSelectedPlano && !$vuetify.breakpoint.smAndDown"
            class="cpm-sidebar"
          >
            <SidebarContent
              :sidebar-items="sidebarItems"
              :all-protocolos-checked="allProtocolosChecked"
              :node-count="nodeCount"
              :visible-entrega-ids.sync="visibleEntregaIds"
              @toggle-all="toggleAllProtocolos"
              :entrega-label-fn="entregalabel"
            />
          </div> -->

          <!-- ── MOBILE: FAB para abrir drawer ── -->
          <v-btn
            v-if="internalSelectedPlano && $vuetify.breakpoint.smAndDown"
            fab
            small
            color="#2D4B95"
            dark
            class="cpm-sidebar-fab"
            @click="sidebarOpen = true"
            :title="'Ver protocolos'"
          >
            <v-icon size="24">mdi-layers-outline</v-icon>
          </v-btn>

          <!-- ── MOBILE: Overlay backdrop ── -->
          <transition name="cpm-overlay-fade">
            <div
              v-if="$vuetify.breakpoint.smAndDown && sidebarOpen"
              class="cpm-drawer-overlay"
              @click="sidebarOpen = false"
            />
          </transition>

          <!-- ── MOBILE: Bottom drawer ── -->
          <transition name="cpm-drawer-slide">
            <div
              v-if="$vuetify.breakpoint.smAndDown && sidebarOpen"
              class="cpm-mobile-drawer"
            >
              <!-- Handle bar -->
              <div class="cpm-mobile-drawer__handle-wrap" @click="sidebarOpen = false">
                <div class="cpm-mobile-drawer__handle"></div>
              </div>

              <!-- Header -->
              <div class="cpm-sidebar__header">
                <v-icon size="14" class="mr-1" color="primary">mdi-layers-outline</v-icon>
                Protocolos
                <v-spacer></v-spacer>
                <button
                  v-if="sidebarItems.length > 0"
                  class="cpm-sidebar__toggle-all"
                  @click="toggleAllProtocolos"
                  :title="allProtocolosChecked ? 'Desmarcar todos' : 'Marcar todos'"
                >
                  {{ allProtocolosChecked ? 'Ninguno' : 'Todos' }}
                </button>
                <button class="cpm-drawer-close-btn ml-2" @click="sidebarOpen = false">
                  <v-icon size="16">mdi-close</v-icon>
                </button>
              </div>

              <!-- List -->
              <div class="cpm-sidebar__list">
                <div
                  v-for="item in sidebarItems"
                  :key="item.id"
                  class="cpm-sidebar__item"
                >
                  <v-checkbox
                    v-model="visibleEntregaIds"
                    :value="item.id"
                    dense
                    hide-details
                    class="mt-0 pt-0 flex-shrink-0"
                  ></v-checkbox>
                  <span class="cpm-sidebar__dot" :style="{ background: item.color || '#2D4B95' }"></span>
                  <span class="cpm-sidebar__label" :title="entregalabel(item)">
                    {{ entregalabel(item) }}
                  </span>
                </div>
                <div v-if="!sidebarItems.length" class="cpm-sidebar__empty">
                  Sin otros emplazamientos
                </div>
              </div>

              <!-- Footer -->
              <div class="cpm-sidebar__footer">
                <span>Puntos definidos:</span>
                <strong>{{ nodeCount }}</strong>
              </div>
            </div>
          </transition>

        </div>

        <!-- Color Picker Dialog -->
        <v-dialog v-model="showColorPicker" max-width="300px">
          <v-card>
            <v-card-title class="text-subtitle-1 py-3 px-4">
              Color del área
              <v-spacer></v-spacer>
              <v-btn icon small @click="showColorPicker = false">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-3">
              <v-color-picker
                v-model="lineColor"
                mode="hexa"
                show-swatches
                swatches-max-height="140px"
                :swatches="colorSwatches"
                elevation="0"
                flat
                hide-inputs
              ></v-color-picker>
            </v-card-text>
            <v-card-actions class="px-4 pb-3">
              <v-spacer></v-spacer>
              <v-btn small depressed @click="showColorPicker = false" style="background-color: #2D4B95; color: white;">Aceptar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-card>
    </v-dialog>

    <!-- ── Capture Frame Overlay ── -->
    <CaptureFrameOverlay
      :visible="showCaptureOverlay"
      :viewer-el="captureViewerEl"
      :viewer-type="isForgeMode ? 'forge' : 'image'"
      @captured="onCaptured"
      @cancel="showCaptureOverlay = false"
      @capture-error="onCaptureError"
    />

  </div>
</template>

<script>
import ForgePolygonViewer from './ForgePolygonViewer.vue'
import ImagePolygonViewer from './ImagePolygonViewer.vue'
import CaptureFrameOverlay from '@/components/Globals/Helpers/CaptureFrameOverlay.vue'

export default {
  name: 'ControlPlanoModal',

  components: { ForgePolygonViewer, ImagePolygonViewer, CaptureFrameOverlay },

  props: {
    planos: {
      type: Array,
      default: () => []
    },
    planoEntrega: {
      type: Object,
      default: null
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    allowPlanoChange: {
      type: Boolean,
      default: false
    },
    noTrigger: {
      type: Boolean,
      default: false
    },
		smallBtn: {
			type: Boolean,
			default: false
		}
  },

  data () {
    return {
      showModal: false,
      showColorPicker: false,
      internalSelectedPlano: null,
      lineColor: '#2D4B95',
      fillOpacity: 0.3,
      loading: false,
      sidebarOpen: false,
			isEditingPolygon: false,
      colorSwatches: [
        ['#2D4B95', '#1976D2', '#0D47A1', '#1565C0', '#0288D1', '#0097A7'],
        ['#388E3C', '#43A047', '#66BB6A', '#4CAF50', '#81C784', '#A5D6A7'],
        ['#F57C00', '#FF9800', '#FFA726', '#FFB74D', '#FFCC80', '#FFE0B2'],
        ['#C2185B', '#D81B60', '#E91E63', '#EC407A', '#F06292', '#F48FB1'],
        ['#7B1FA2', '#8E24AA', '#9C27B0', '#AB47BC', '#BA68C8', '#CE93D8'],
        ['#5D4037', '#6D4C41', '#795548', '#8D6E63', '#A1887F', '#BCAAA4'],
        ['#303030', '#424242', '#616161', '#757575', '#9E9E9E', '#BDBDBD']
      ],
      visibleEntregaIds: [],
      viewerZoomLevel: 1,
      viewerNodeCount: 0,
      // ── Capture ──────────────────────────────────
      showCaptureOverlay: false,
      captureViewerEl: null,
      pendingSaveData: null       // guarda el resultado de viewer.savePlano() mientras capturamos
    }
  },

  computed: {
		allowedEditing () {
      if (this.viewOnly) return false
      if (!this.isForgeMode) return true
      return this.isEditingPolygon
		},
    selectedPlanoObj () {
      if (!this.internalSelectedPlano || !this.planos.length) return null
      return this.planos.find(p => p.id === this.internalSelectedPlano) || null
    },

    isForgeMode () {
      return !!(this.selectedPlanoObj?.file?.forge_file?.urn || this.selectedPlanoObj?.forge_file?.urn)
    },

    selectedPlanoForgeUrn () {
      return this.selectedPlanoObj?.file?.forge_file?.urn || this.selectedPlanoObj?.forge_file?.urn || null
    },

    forgeViewerPlanoData () {
      if (!this.isForgeMode) return null
      const existing = this._existingDataForCurrentPlano()
      return {
        plano_id: this.internalSelectedPlano,
        nodes:    existing?.nodes || [],
        color:    this.lineColor,
        opacity:  this.fillOpacity
      }
    },

    imageViewerPlanoData () {
      const existing = this._existingDataForCurrentPlano()
      if (!existing || !existing.nodes || !existing.nodes.length) return null
      return {
        nodes:   existing.nodes,
        color:   this.lineColor,
        opacity: this.fillOpacity
      }
    },

    sidebarItems () {
      const entregas = this.selectedPlanoObj?.plano_entregas || []
      const currentEntregaId = this.planoEntrega?.entrega_id || null
      return entregas.filter(pe =>
        pe.nodes && pe.nodes.length > 0 &&
        pe.entrega_id !== currentEntregaId
      )
    },

    visibleAllPolygons () {
      return this.sidebarItems
        .filter(pe => this.visibleEntregaIds.includes(pe.id))
        .map(pe => ({
          nodes:   pe.nodes,
          color:   pe.color || '#2D4B95',
          opacity: pe.opacity != null ? pe.opacity : 0.3
        }))
    },

    nodeCount () {
      return this.viewerNodeCount
    },

    allProtocolosChecked () {
      return this.sidebarItems.length > 0 &&
        this.sidebarItems.every(pe => this.visibleEntregaIds.includes(pe.id))
    }
  },

  watch: {
    internalSelectedPlano () {
      this._initSidebarIds()
      this.viewerZoomLevel = 1
      this.viewerNodeCount = 0
      this.sidebarOpen = false
			this.isEditingPolygon = false
    }
  },

  methods: {
    openModal () {
      if (this.planoEntrega) {
        this.lineColor   = this.planoEntrega.color   || '#2D4B95'
        this.fillOpacity = this.planoEntrega.opacity != null ? this.planoEntrega.opacity : 0.3
      } else {
        this.lineColor   = '#2D4B95'
        this.fillOpacity = 0.3
      }

      this.internalSelectedPlano = this._inferPlanoId()
      this.viewerZoomLevel = 1
      this.viewerNodeCount = this.planoEntrega?.nodes?.length || 0
      this.sidebarOpen = false
			this.isEditingPolygon = false
      this._initSidebarIds()
      this.showModal = true
    },

    closeModal () {
      this.showModal = false
      this.showCaptureOverlay = false
      this.sidebarOpen = false
			this.isEditingPolygon = false
      this.$emit('close')
    },

    // ─────────────────────────────────────────────────────────────────────────
    // FLUJO DE GUARDADO CON CAPTURA
    // 1. triggerSave → valida polígono → llama viewer.savePlano()
    // 2. onSubViewerSave → guarda datos temporalmente → abre CaptureFrameOverlay
    // 3. onCaptured → une datos + screenshot → emite 'save-plano' → cierra modal
    // ─────────────────────────────────────────────────────────────────────────
		getCanvasEl () {
			return this.$refs.canvas || null
		},

    triggerSave () {
      const viewer = this.$refs.viewer
      if (!viewer) return

      if (this.isForgeMode && !viewer.hasMarkupData) {
        this._showError('Dibuje un polígono antes de guardar')
        return
      }
      if (!this.isForgeMode && (!viewer.nodes || viewer.nodes.length < 3)) {
        this._showError('El polígono necesita al menos 3 puntos')
        return
      }

      this.loading = true
      viewer.savePlano()
    },

    // Recibe los datos del polígono desde el sub-viewer; en lugar de emitir
    // directamente, abre el overlay de captura.
    onSubViewerSave (data) {
      this.pendingSaveData = {
        ...data,
        plano_id: this.internalSelectedPlano
      }
      this.loading = false

      // Determina el elemento DOM del viewer activo para pasárselo al overlay
      this.$nextTick(() => {
        this.captureViewerEl = this._resolveViewerEl()
        this.showCaptureOverlay = true
      })
    },

    // El usuario confirmó el encuadre y el overlay entrega el File con la captura
    onCaptured (screenshotFile) {
      this.showCaptureOverlay = false

      const finalData = {
        ...this.pendingSaveData,
        screenshot: screenshotFile     // File PNG listo para enviar a la API
      }
      this.pendingSaveData = null

      this.$emit('save-plano', finalData)
			this._showSuccess('Emplazamiento guardado correctamente')
      this.showModal = false
    },

    onCaptureError (err) {
      console.error('[ControlPlanoModal] Error en captura:', err)
      this._showError('No se pudo capturar la imagen del emplazamiento')
      // Guardar de todas formas sin screenshot
      if (this.pendingSaveData) {
        this.$emit('save-plano', { ...this.pendingSaveData, screenshot: null })
        this.pendingSaveData = null
        this.showCaptureOverlay = false
        this.showModal = false
      }
    },

    // Obtiene el elemento DOM correcto del viewer visible
    _resolveViewerEl () {
      const viewer = this.$refs.viewer
      if (!viewer) return this.$refs.viewerWrapper || null

      if (this.isForgeMode) {
        // Para ForgePolygonViewer devolvemos el div del viewer de Autodesk
        const forgeDiv = viewer.$el
          ? viewer.$el.querySelector(`[id^="forgeViewer-reusable-"]`) || viewer.$el
          : null
        // También inyectamos la instancia de forgeViewer para que el overlay pueda usar getScreenShot
        if (forgeDiv && viewer.forgeViewer) {
          forgeDiv.__forgeViewerInstance = viewer.forgeViewer
        }
        return forgeDiv
      } else {
        // Para ImagePolygonViewer devolvemos el canvas
				return viewer.getCanvasEl?.() || viewer.$el?.querySelector('canvas') || viewer.$el
      }
    },

    // ─────────────────────────────────────────────────────────────────────────

    planoIsForge (plano) {
      return !!(plano && plano.file?.forge_file && plano.file?.forge_file)
    },

    toggleAllProtocolos () {
      if (this.allProtocolosChecked) {
        this.visibleEntregaIds = []
      } else {
        this.visibleEntregaIds = this.sidebarItems.map(pe => pe.id)
      }
    },

    _initSidebarIds () {
      this.$nextTick(() => {
        this.visibleEntregaIds = this.sidebarItems.map(pe => pe.id)
      })
    },

    entregalabel (pe) {
      if (!pe) return '—'
      if (pe.entrega) {
        return pe.entrega.protocolo_name || pe.entrega.title || `Entrega #${pe.entrega_id}`
      }
      return `Entrega #${pe.entrega_id}`
    },

    zoomIn () {
      if (this.$refs.viewer?.zoomIn) {
        this.$refs.viewer.zoomIn()
        this.viewerZoomLevel = this.$refs.viewer.zoomLevel
      }
    },

    zoomOut () {
      if (this.$refs.viewer?.zoomOut) {
        this.$refs.viewer.zoomOut()
        this.viewerZoomLevel = this.$refs.viewer.zoomLevel
      }
    },

    clearCurrentPolygon () {
      const viewer = this.$refs.viewer
      if (!viewer) return

      if (this.isForgeMode) {
        if (viewer.stopShapeWatcher) viewer.stopShapeWatcher()
        if (viewer._clearLayer) viewer._clearLayer()
        viewer.hasMarkupData = false
        if (viewer.$nextTick && viewer.setToolMode) {
					viewer.$nextTick(() => viewer.setToolMode(this.allowedEditing ? 'draw' : 'none'))
        }
        this.viewerNodeCount = 0
        return
      }

      if (viewer) {
        viewer.nodes = []
        if (viewer.redraw) viewer.redraw()
        this.viewerNodeCount = 0
      }
    },

    _existingDataForCurrentPlano () {
      if (!this.planoEntrega || !this.internalSelectedPlano) return null
      return this.planoEntrega.plano_id === this.internalSelectedPlano ? this.planoEntrega : null
    },

    _inferPlanoId () {
      return this.planoEntrega?.plano_id || null
    },

    _showError (message) {
      this.$store.globalStore.dispatch('addMessage', {
        message, title: 'Error', created: new Date(), type: 'error',
        icon: 'mdi-alert-circle', color: '#fb8c00'
      })
    },

		_showSuccess (message) {
			this.$store.globalStore.dispatch('addMessage', {
				message, title: 'Éxito', created: new Date(), type: 'success',
				icon: 'mdi-check-circle', color: '#3EBFA3'
			})
		},

		togglePolygonEdit () {
      const nextValue = !this.isEditingPolygon
      if (!nextValue) {
        const viewer = this.$refs.viewer
        if (viewer && typeof viewer.finishPolygonDrawing === 'function') {
          viewer.finishPolygonDrawing()
        }
      }
      this.isEditingPolygon = nextValue
		},

		onPolygonClosed (){
			this.isEditingPolygon = false
		}
  }
}
</script>

<style scoped>
/* ── Trigger Button ── */
.plano-trigger-btn {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 7px;
  border: 1.5px solid #2D4B95;
  background: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #2D4B95;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  gap: 2px;
}
.plano-trigger-btn:hover { background: #F0F4FF; }
.plano-trigger-btn--active { background: #2D4B95; border-color: #2D4B95; color: white; }
.plano-trigger-btn--active:hover { background: #253d7a; border-color: #253d7a; }
.plano-trigger-btn--small {
  padding: 4px 6px;
  border-radius: 5px;
  min-width: unset;
}
/* ── Toolbar ── */
.app-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  min-height: 48px;
  max-height: 48px;
  padding: 0 16px;
  background-color: #2D4B95;
  flex-shrink: 0;
}
.app-toolbar__title {
  display: flex;
  align-items: center;
  color: white;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
}
.app-toolbar__actions { display: flex; align-items: center; gap: 8px; }
.app-toolbar__btn {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  outline: none;
  transition: opacity 0.15s ease, background 0.15s ease;
  white-space: nowrap;
  letter-spacing: 0.02em;
}
.app-toolbar__btn:disabled { opacity: 0.45; cursor: not-allowed; }
.app-toolbar__btn--outline {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.45);
  color: white;
}
.app-toolbar__btn--outline:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); }
.app-toolbar__btn--solid { background: #5E85EF; color: white; }
.app-toolbar__btn--solid:hover:not(:disabled) { background: #4a72e0; }

/* ── Color Swatch ── */
.color-swatch {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}
.color-swatch:hover { transform: scale(1.15); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); }

/* ── Placeholder ── */
.cpm-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  pointer-events: none;
}

/* ── Zoom buttons ── */
.cpm-zoom-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid #d0d0d0;
  background: #fff;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}
.cpm-zoom-btn:hover:not([disabled]) { background: #f0f4ff; border-color: #2D4B95; }
.cpm-zoom-btn[disabled] { opacity: 0.4; cursor: not-allowed; }

/* ── Hint chips ── */
.cpm-hint {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.cpm-hint--blue  { background: #e8edf8; color: #2D4B95; }
.cpm-hint--grey  { background: #f0f0f0; color: #555; }
.cpm-hint--red   { background: #fdecea; color: #c62828; }

/* ── Clear button ── */
.cpm-clear-btn, .cpm-edit-btn {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid #ef9a9a;
  background: #fff5f5;
  color: #c62828;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s;
  white-space: nowrap;
}
.cpm-clear-btn:hover:not([disabled]) { background: #fdecea; }
.cpm-clear-btn[disabled],.cpm-edit-btn[disabled] { opacity: 0.38; cursor: not-allowed; border-color: #e0e0e0; background: #f5f5f5; color: #bdbdbd; }

.cpm-edit-btn {
	border: 1px solid #2D4B95;
	background: #f0f4ff;
	color: #2D4B95;
}
.cpm-edit-btn:hover:not([disabled]) { background: #e8edf8; }

/* ── Desktop Sidebar ── */
.cpm-sidebar {
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e0e0e0;
  background: #fafafa;
  overflow: hidden;
  flex-shrink: 0;
}
.cpm-sidebar__header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #2D4B95;
  border-bottom: 1px solid #e0e0e0;
  background: #f0f4ff;
  flex-shrink: 0;
}
.cpm-sidebar__toggle-all {
  font-size: 10px;
  font-weight: 500;
  color: #2D4B95;
  text-transform: none;
  letter-spacing: 0;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid #a3b8e8;
  background: #fff;
  cursor: pointer;
  transition: background 0.12s;
}
.cpm-sidebar__toggle-all:hover { background: #e8edf8; }
.cpm-sidebar__list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}
.cpm-sidebar__item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.1s;
}
.cpm-sidebar__item:hover { background: #f0f0f0; }
.cpm-sidebar__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid rgba(0,0,0,0.12);
}
.cpm-sidebar__label {
  font-size: 11px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}
.cpm-sidebar__empty {
  padding: 16px 12px;
  text-align: center;
  color: #9e9e9e;
  font-size: 11px;
}
.cpm-sidebar__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  font-size: 11px;
  color: #666;
  border-top: 1px solid #e0e0e0;
  background: #f5f5f5;
  flex-shrink: 0;
}

/* ── Mobile FAB ── */
.cpm-sidebar-fab {
  position: absolute !important;
  bottom: 16px;
  right: 16px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(45, 75, 149, 0.35) !important;
}

/* ── Mobile Overlay ── */
.cpm-drawer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.32);
  z-index: 20;
}
.cpm-overlay-fade-enter-active,
.cpm-overlay-fade-leave-active { transition: opacity 0.22s ease; }
.cpm-overlay-fade-enter,
.cpm-overlay-fade-leave-to { opacity: 0; }

/* ── Mobile Bottom Drawer ── */
.cpm-mobile-drawer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  background: #fff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  overflow: hidden;
}
.cpm-mobile-drawer__handle-wrap {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
  cursor: pointer;
}
.cpm-mobile-drawer__handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #d0d0d0;
}

/* Drawer slide transition */
.cpm-drawer-slide-enter-active,
.cpm-drawer-slide-leave-active { transition: transform 0.26s cubic-bezier(0.32, 0.72, 0, 1); }
.cpm-drawer-slide-enter,
.cpm-drawer-slide-leave-to { transform: translateY(100%); }

/* Close button inside drawer header */
.cpm-drawer-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid #d0d0d0;
  background: #fff;
  cursor: pointer;
  color: #555;
  transition: background 0.12s;
}
.cpm-drawer-close-btn:hover { background: #f0f0f0; }
</style>